import React, { useEffect, useState } from 'react';
import supabase from '../../supabase/supabase';
import { useAppData } from '../../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';

export default function AdminDashboard() {
  const { profile } = useAppData();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    if (profile?.role === 'admin') {
      fetchUsers();
    }
  }, [profile]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('profiles_20240520')
        .select('*')
        .order('role', { ascending: false });
      
      if (fetchError) throw fetchError;
      setUsers(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!confirm(`Are you absolutely sure you want to delete ${email}? This action cannot be undone.`)) return;

    try {
      setLoading(true);
      const { error } = await supabase.rpc('delete_user_by_admin', { target_user_id: userId });
      if (error) throw error;
      fetchUsers();
    } catch (err) {
      alert(`Error: ${err.message}`);
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.full_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (profile?.role !== 'admin') {
    return (
      <div className="h-full flex items-center justify-center bg-app-bg p-8">
        <div className="text-center space-y-4 max-w-sm">
          <SafeIcon icon={FiIcons.FiShieldOff} className="text-5xl text-app-red mx-auto opacity-50" />
          <h2 className="text-xl font-bold text-app-red uppercase tracking-widest">Access Denied</h2>
          <div className="bg-app-surface p-6 rounded-xl border border-app-border text-left shadow-2xl">
            <p className="text-app-muted text-[10px] font-black uppercase tracking-widest mb-3">System Diagnostics</p>
            <div className="space-y-2 font-mono text-xs">
              <p className="flex justify-between"><span>Current Role:</span> <span className="text-app-red font-bold">{profile?.role || 'null'}</span></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-app-bg p-4 md:p-8 overflow-y-auto">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3 text-white">
              <SafeIcon icon={FiIcons.FiShield} className="text-app-accent" />
              User Control Center
            </h1>
            <p className="text-app-muted mt-1">Manage platform access and security levels</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <FiIcons.FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-app-muted" />
              <input 
                type="text" 
                placeholder="Search name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded-lg text-sm w-48 md:w-64 focus:border-app-accent outline-none"
              />
            </div>
            <button 
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 bg-app-accent text-black px-4 py-2 rounded-lg text-sm font-bold hover:bg-yellow-500 transition-colors shadow-lg"
            >
              <FiIcons.FiUserPlus /> Add User
            </button>
            <button 
              onClick={fetchUsers}
              className="p-2.5 bg-app-surface border border-app-border rounded-lg hover:border-app-accent transition-all text-app-text"
            >
              <FiIcons.FiRefreshCw className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        <div className="bg-app-surface border border-app-border rounded-xl overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-app-bg/50 text-app-muted text-[10px] uppercase tracking-widest border-b border-app-border">
                  <th className="px-6 py-4 font-bold">User</th>
                  <th className="px-6 py-4 font-bold">Email</th>
                  <th className="px-6 py-4 font-bold">Permissions</th>
                  <th className="px-6 py-4 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-app-border/30">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-app-bg border border-app-border overflow-hidden flex items-center justify-center">
                            {user.avatar_url ? (
                              <img src={user.avatar_url} className="w-full h-full object-cover" alt="" />
                            ) : (
                              <FiIcons.FiUser className="text-app-muted" size={18} />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-app-text">{user.full_name || user.email.split('@')[0]}</p>
                            {!user.full_name && <p className="text-[10px] text-app-red/50 uppercase font-black">Name Not Set</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-app-muted font-mono text-xs">{user.email}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                          user.role === 'admin' 
                            ? 'bg-app-accent text-black border-app-accent' 
                            : 'bg-app-bg text-app-muted border-app-border'
                        }`}>
                          {user.role === 'admin' && <FiIcons.FiShield size={10} />}
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-1">
                          <button 
                            onClick={() => setEditingUser(user)}
                            className="p-2 text-app-muted hover:text-white hover:bg-white/5 rounded-lg transition-all"
                            title="Edit User"
                          >
                            <FiIcons.FiEdit2 size={16} />
                          </button>
                          <button 
                            onClick={() => handleDeleteUser(user.id, user.email)}
                            className="p-2 text-app-muted hover:text-app-red hover:bg-app-red/10 rounded-lg transition-all"
                            title="Delete User"
                          >
                            <FiIcons.FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : !loading && (
                  <tr>
                    <td colSpan="4" className="px-6 py-16 text-center text-app-muted italic">
                      No users found matching "{searchTerm}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {loading && (
            <div className="p-16 text-center text-app-muted bg-app-surface/50">
              <FiIcons.FiLoader className="animate-spin inline-block mb-3 text-app-accent text-3xl" />
              <p className="text-xs font-mono uppercase tracking-widest">Accessing Secure Records...</p>
            </div>
          )}
        </div>
      </div>
      
      {showAddModal && <AddUserModal onClose={() => setShowAddModal(false)} onSuccess={() => {setShowAddModal(false); fetchUsers();}} />}
      {editingUser && <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} onSuccess={() => {setEditingUser(null); fetchUsers();}} />}
    </div>
  );
}